import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const PartnerPerformance = ({ selectedPeriod, isSecureView }) => {
  const [selectedPartner, setSelectedPartner] = useState('all');

  const partnerData = [
    {
      id: 'partner-1',
      name: 'Senior Partner A',
      billingTarget: 12000000,
      billingActual: 13500000,
      clientOrigination: 8,
      caseWinRate: 92,
      billableHours: 1850,
      newBusinessValue: 4200000,
      satisfaction: 96,
      performance: 'excellent'
    },
    {
      id: 'partner-2',
      name: 'Senior Partner B',
      billingTarget: 10000000,
      billingActual: 10800000,
      clientOrigination: 6,
      caseWinRate: 88,
      billableHours: 1720,
      newBusinessValue: 3100000,
      satisfaction: 94,
      performance: 'excellent'
    },
    {
      id: 'partner-3',
      name: 'Partner C',
      billingTarget: 8500000,
      billingActual: 8200000,
      clientOrigination: 4,
      caseWinRate: 85,
      billableHours: 1650,
      newBusinessValue: 2400000,
      satisfaction: 91,
      performance: 'good'
    },
    {
      id: 'partner-4',
      name: 'Partner D',
      billingTarget: 7000000,
      billingActual: 7800000,
      clientOrigination: 5,
      caseWinRate: 87,
      billableHours: 1580,
      newBusinessValue: 2800000,
      satisfaction: 93,
      performance: 'excellent'
    }
  ];

  const benchmarkData = [
    { metric: 'Billing Performance', industry: 85, firm: 92 },
    { metric: 'Client Origination', industry: 70, firm: 88 },
    { metric: 'Case Win Rate', industry: 75, firm: 88 },
    { metric: 'Client Satisfaction', industry: 80, firm: 94 },
    { metric: 'Efficiency', industry: 78, firm: 86 },
    { metric: 'Business Development', industry: 65, firm: 82 }
  ];

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-warning-600';
      case 'needs-improvement': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getPerformanceBg = (performance) => {
    switch (performance) {
      case 'excellent': return 'bg-success-50 border-success-200';
      case 'good': return 'bg-warning-50 border-warning-200';
      case 'needs-improvement': return 'bg-error-50 border-error-200';
      default: return 'bg-secondary-50 border-border';
    }
  };

  const formatCurrency = (value) => {
    if (!isSecureView) return 'KES ••••••••';
    return `KES ${(value / 1000000).toFixed(1)}M`;
  };

  const getTargetAchievement = (actual, target) => {
    return Math.round((actual / target) * 100);
  };

  const selectedPartnerData = selectedPartner === 'all' 
    ? partnerData 
    : partnerData?.filter(p => p?.id === selectedPartner);

  return (
    <div className="bg-surface rounded-lg shadow-card border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Partner Performance</h2>
          <div className="flex items-center space-x-3">
            {!isSecureView && (
              <div className="flex items-center space-x-1 text-warning-600">
                <Icon name="Lock" size={14} />
                <span className="text-xs">Limited View</span>
              </div>
            )}
            <select 
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target?.value)}
              className="bg-surface border border-border rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Partners</option>
              {partnerData?.map((partner) => (
                <option key={partner?.id} value={partner?.id}>
                  {partner?.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="text-sm text-text-secondary">
          Individual performance metrics and firm benchmarks for {selectedPeriod} period
        </p>
      </div>

      <div className="p-6">
        {/* Partner Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          {selectedPartnerData?.map((partner) => (
            <div 
              key={partner?.id} 
              className={`p-4 rounded-lg border-2 ${getPerformanceBg(partner?.performance)}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">{partner?.name}</h3>
                    <span className={`text-xs font-medium capitalize ${getPerformanceColor(partner?.performance)}`}>
                      {partner?.performance} Performance
                    </span>
                  </div>
                </div>
                <Icon name="Award" size={20} className={getPerformanceColor(partner?.performance)} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-text-secondary">Billing Target</p>
                  <p className="text-sm font-semibold text-text-primary">{formatCurrency(partner?.billingTarget)}</p>
                  <div className="w-full bg-secondary-200 rounded-full h-1.5 mt-1">
                    <div
                      className="bg-primary h-1.5 rounded-full"
                      style={{ width: `${Math.min(getTargetAchievement(partner?.billingActual, partner?.billingTarget), 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-success mt-1">
                    {getTargetAchievement(partner?.billingActual, partner?.billingTarget)}% achieved
                  </p>
                </div>

                <div>
                  <p className="text-xs text-text-secondary">New Clients</p>
                  <p className="text-sm font-semibold text-text-primary">{partner?.clientOrigination}</p>
                  <p className="text-xs text-text-secondary mt-1">Client origination</p>
                </div>

                <div>
                  <p className="text-xs text-text-secondary">Win Rate</p>
                  <p className="text-sm font-semibold text-text-primary">{partner?.caseWinRate}%</p>
                  <p className="text-xs text-text-secondary mt-1">{partner?.billableHours} billable hrs</p>
                </div>

                <div>
                  <p className="text-xs text-text-secondary">New Business</p>
                  <p className="text-sm font-semibold text-text-primary">{formatCurrency(partner?.newBusinessValue)}</p>
                  <p className="text-xs text-text-secondary mt-1">{partner?.satisfaction}% satisfaction</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Benchmark Comparison */}
        <div className="mb-6">
          <h3 className="text-md font-semibold text-text-primary mb-4">Firm vs Industry Benchmarks</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={benchmarkData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis 
                  angle={30} 
                  domain={[0, 100]} 
                  tick={{ fontSize: 10 }}
                />
                <Radar
                  name="Industry Average"
                  dataKey="industry"
                  stroke="#94A3B8"
                  fill="#94A3B8"
                  fillOpacity={0.2}
                />
                <Radar
                  name="Our Firm"
                  dataKey="firm"
                  stroke="#6366F1"
                  fill="#6366F1"
                  fillOpacity={0.4}
                />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="bg-secondary-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <h4 className="text-sm font-semibold text-text-primary">Performance Summary</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-text-secondary">Avg. Target Achievement:</span>
              <span className="ml-2 font-semibold text-success">108%</span>
            </div>
            <div>
              <span className="text-text-secondary">Top Performer:</span>
              <span className="ml-2 font-semibold text-text-primary">Senior Partner A</span>
            </div>
            <div>
              <span className="text-text-secondary">Firm vs Industry:</span>
              <span className="ml-2 font-semibold text-success">+12% above average</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerPerformance;